import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../api';

function formatMeta(meta) {
  if (!meta) return '-';
  if (typeof meta === 'string') return meta;
  try {
    return JSON.stringify(meta, null, 2);
  } catch (_error) {
    return String(meta);
  }
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(date);
}

export function LogsPage() {
  const [rows, setRows] = useState([]);
  const [scope, setScope] = useState('');
  const [level, setLevel] = useState('');
  const [availableScopes, setAvailableScopes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scopesLoading, setScopesLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 100;
  const [total, setTotal] = useState(0);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total]);

  async function load(currentScope = scope, currentLevel = level, currentPage = page) {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (currentScope.trim()) params.set('scope', currentScope.trim());
      if (currentLevel.trim()) params.set('level', currentLevel.trim());
      params.set('page', String(currentPage));
      params.set('pageSize', String(pageSize));
      const data = await api(`/logs?${params.toString()}`);
      setRows(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total || 0));
      setPage(Number(data?.page || currentPage || 1));
    } finally {
      setLoading(false);
    }
  }

  async function loadScopes() {
    setScopesLoading(true);
    try {
      const data = await api('/logs/scopes');
      setAvailableScopes(Array.isArray(data?.items) ? data.items : []);
    } finally {
      setScopesLoading(false);
    }
  }

  useEffect(() => {
    load().catch((currentError) => setError(currentError.message || 'Не удалось загрузить логи'));
    loadScopes().catch(() => {});
  }, []);

  return (
    <section className="card">
      <div className="section-header">
        <h2>Логи</h2>
        <div className="inline-form compact">
          <select value={level} onChange={(e) => setLevel(e.target.value)}>
            <option value="">Все уровни</option>
            <option value="info">info</option>
            <option value="error">error</option>
            <option value="warn">warn</option>
          </select>
          <button type="button" onClick={() => load(scope, level, 1).catch((currentError) => setError(currentError.message || 'Не удалось загрузить логи'))}>Применить</button>
        </div>
      </div>

      <div className="logs-scope-tags">
        <button
          type="button"
          className={`scope-tag${scope === '' ? ' active' : ''}`}
          onClick={() => { setScope(''); load('', level, 1).catch((currentError) => setError(currentError.message || 'Не удалось загрузить логи')); }}
        >
          Все
        </button>
        {availableScopes.map((item) => (
          <button
            key={item.scope}
            type="button"
            className={`scope-tag${scope === item.scope ? ' active' : ''}`}
            onClick={() => { setScope(item.scope); load(item.scope, level, 1).catch((currentError) => setError(currentError.message || 'Не удалось загрузить логи')); }}
          >
            {item.scope}
            <span>{item.count}</span>
          </button>
        ))}
        {scopesLoading ? <span className="muted">Обновление scope...</span> : null}
      </div>

      {error ? <div className="error-banner section-banner">{error}</div> : null}
      {loading ? <div className="muted">Загрузка...</div> : null}

      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>ID</th><th>Level</th><th>Scope</th><th>Message</th><th>Meta</th><th>Date</th></tr></thead>
          <tbody>
            {rows.map((item) => (
              <tr key={item.id}>
                <td>{item.id}</td>
                <td>{item.level}</td>
                <td>{item.scope}</td>
                <td>{item.message}</td>
                <td><code
                    className="log-meta">{item.meta_json ? JSON.stringify(item.meta_json).slice(0, 500) : '-'}</code>
                </td>
                <td>{formatDate(item.created_at)}</td>
              </tr>
            ))}
            {!rows.length && !loading ? <tr><td colSpan="6">Логи пока отсутствуют</td></tr> : null}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <div className="pagination-controls">
          <button type="button" disabled={loading || page <= 1} onClick={() => load(scope, level, page - 1).catch((currentError) => setError(currentError.message || 'Не удалось загрузить логи'))}>Назад</button>
          <span className="pagination-info">Страница {page} из {totalPages}</span>
          <button type="button" disabled={loading || page >= totalPages} onClick={() => load(scope, level, page + 1).catch((currentError) => setError(currentError.message || 'Не удалось загрузить логи'))}>Вперёд</button>
        </div>
        <div className="muted">Всего записей: {total}</div>
      </div>
    </section>
  );
}
